import React from 'react';
import classnames from 'classnames';
import { DateTime } from 'luxon';
import type { ActorType } from 'BlockList/types';
import { Tooltip } from 'Shared/components';
import { formatCreatedBy } from 'BlockList/utils/formatCreatedBy';
import styles from './NotesCell.module.css';

type NotesCellProps = {
  notes: string | null | undefined;
  createdAt: string | null | undefined;
  createdByType: ActorType;
  // Dims only the icon button — never the tooltip popover-wrapper, which is
  // a sibling inside Tooltip's .trigger. Applying opacity higher up (on the
  // row or cell) would cascade into the popover and make the card transparent.
  isInactive?: boolean;
};

const formatFooterDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso, { zone: 'utc' });
  return dt.isValid ? dt.toFormat('LLLL d, yyyy') : null;
};

export const NotesCell: React.FC<NotesCellProps> = ({
  notes,
  createdAt,
  createdByType,
  isInactive,
}) => {
  if (!notes || notes.trim() === '') {
    return (
      <span className={isInactive ? styles.iconDimmed : undefined}>—</span>
    );
  }

  const footerDate = formatFooterDate(createdAt);
  const creator = formatCreatedBy(createdByType);

  const tooltipContent = (
    <div className={styles.card}>
      <div className={styles.header}>NOTE</div>
      <div className={styles.body}>{notes}</div>
      {footerDate && (
        <>
          <hr className={styles.divider} />
          <div className={styles.footer}>
            {creator
              ? `Added by ${creator} on ${footerDate}`
              : `Added on ${footerDate}`}
          </div>
        </>
      )}
    </div>
  );

  return (
    <Tooltip
      tooltip={tooltipContent}
      position="top"
      popoverClassName={styles.popoverReset}
    >
      <button
        type="button"
        aria-label="View notes"
        className={classnames(styles.trigger, {
          [styles.iconDimmed]: isInactive,
        })}
      >
        💬
      </button>
    </Tooltip>
  );
};
